function createElement(tagName, className = '', text = null) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== null) element.textContent = text;
  return element;
}

function createMetric(label, value, unit = '') {
  const metric = document.createElement('div');
  const strong = document.createElement('strong');
  strong.textContent = value;
  if (unit) strong.append(createElement('span', 'cost-kpi-unit', unit));
  metric.append(createElement('span', '', label), strong);
  return metric;
}

function createKpiCard({ accent, icon, title, left, right, sub, percent, formatPercent, formatProgressValue }) {
  const card = createElement('article', `cost-kpi-card accent-${accent}`);
  const header = createElement('div', 'cost-kpi-header');
  header.append(
    createElement('span', 'cost-kpi-icon', icon),
    createElement('span', 'cost-kpi-title', title),
  );
  const metrics = createElement('div', 'cost-kpi-metrics');
  metrics.append(
    createMetric(left.label, left.value, left.unit),
    createElement('div', 'cost-kpi-divider'),
    createMetric(right.label, right.value, right.unit),
  );
  const subline = createElement('div', 'cost-kpi-sub');
  if (sub.strong) {
    subline.append(document.createTextNode(sub.text), createElement('strong', '', sub.strong));
  } else {
    subline.textContent = sub.text;
  }
  const progress = createElement('progress', `cost-progress ${accent}`);
  progress.max = 100;
  progress.value = formatProgressValue(percent);
  card.append(
    header,
    metrics,
    subline,
    progress,
    createElement('div', 'cost-progress-label', `已使用 ${formatPercent(percent)}`),
  );
  return card;
}

export function renderCostOverviewView(root, {
  snapshot,
  formatCurrency,
  formatCount,
  formatPercent,
  formatProgressValue,
}) {
  if (!root) return;
  const section = createElement('section', 'cost-section-block');
  const titleRow = createElement('div', 'cost-section-title-row');
  const titleGroup = document.createElement('div');
  titleGroup.append(createElement('h3', '', '费用总览'), createElement('span', '', '按费用类型分组'));
  titleRow.append(titleGroup);

  const grid = createElement('div', 'cost-kpi-grid');
  grid.append(
    createKpiCard({
      accent: 'purple',
      icon: '📸',
      title: '图片算力（LinkFox）',
      left: { label: '单次成本', value: formatCurrency(snapshot.imageUnitCost) },
      right: { label: '本期预估', value: formatCurrency(snapshot.imageEstimate) },
      sub: { text: `充值 ${formatCurrency(snapshot.imageFee)} / 算力 ${formatCount(snapshot.imageUnits)}` },
      percent: snapshot.imageUsagePercent,
      formatPercent,
      formatProgressValue,
    }),
    createKpiCard({
      accent: 'blue',
      icon: '🧭',
      title: `市场工具（${snapshot.marketShortLabel}）`,
      left: { label: '单次成本', value: formatCurrency(snapshot.marketUnitCost) },
      right: { label: '本期预估', value: formatCurrency(snapshot.marketEstimate) },
      sub: { text: `${snapshot.marketCycleLabel} ${formatCurrency(snapshot.activeMarket.totalFee)} / 可用 ${formatCount(snapshot.activeMarket.totalCalls)} 次 + 结余 ${formatCount(snapshot.activeMarket.openingBalance)}` },
      percent: snapshot.marketUsagePercent,
      formatPercent,
      formatProgressValue,
    }),
    createKpiCard({
      accent: 'teal',
      icon: '🤖',
      title: `模型费用（${snapshot.selectedModelLabel}）`,
      left: { label: '输入', value: formatCurrency(snapshot.selectedModelConfig.inputPerMillion || 0), unit: '/百万tok' },
      right: { label: '输出', value: formatCurrency(snapshot.selectedModelConfig.outputPerMillion || 0), unit: '/百万tok' },
      sub: { text: '本期预估 ', strong: formatCurrency(snapshot.modelEstimate) },
      percent: snapshot.modelUsagePercent,
      formatPercent,
      formatProgressValue,
    }),
  );
  const total = createElement('div', 'cost-total-card');
  total.append(
    createElement('span', '', '本期合计预估'),
    createElement('strong', '', formatCurrency(snapshot.totalEstimate)),
    createElement('small', '', `图片 ${formatCurrency(snapshot.imageEstimate)} + 市场 ${formatCurrency(snapshot.marketEstimate)} + 模型 ${formatCurrency(snapshot.modelEstimate)}`),
  );
  section.append(titleRow, grid, total);
  root.replaceChildren(section);
}

export function renderCostFormulaStrip(root, { totalFee, denominator, unitCost, formatCurrency, formatCount }) {
  if (!root) return;
  root.replaceChildren(
    createElement('span', '', '自动计算结果'),
    createElement('strong', '', `单次成本 = ${formatCurrency(totalFee)} ÷ ${formatCount(denominator)} = ${formatCurrency(unitCost)}/次`),
  );
}

function createLimitResourceForm(resource, limit = {}) {
  const section = createElement('section', 'cost-limit-form-card');
  section.dataset.resource = resource.key;
  const toggleLabel = createElement('label', 'filter-checkbox');
  const enabled = document.createElement('input');
  enabled.type = 'checkbox';
  enabled.dataset.field = 'enabled';
  enabled.checked = Boolean(limit.enabled);
  toggleLabel.append(enabled, createElement('span', '', resource.label));

  const periodField = createElement('div', 'modal-field');
  periodField.append(createElement('label', '', '周期'));
  const period = createElement('select', 'cost-select');
  period.dataset.field = 'periodType';
  [['daily', '每日'], ['monthly', '每月']].forEach(([value, label]) => {
    const option = createElement('option', '', label);
    option.value = value;
    option.selected = (limit.periodType || 'daily') === value;
    period.append(option);
  });
  periodField.append(period);

  const limitField = createElement('div', 'modal-field');
  limitField.append(createElement('label', '', `上限（${resource.unit}）`));
  const input = createElement('input', 'cost-input');
  input.dataset.field = 'limitUnits';
  input.type = 'number';
  input.min = '0';
  input.step = '1';
  input.value = String(Number(limit.limitUnits || 0));
  limitField.append(input);
  section.append(toggleLabel, periodField, limitField);
  return section;
}

export function createUsageLimitDialogView({ title, resources, seedLimits }) {
  const dialog = document.createElement('div');
  const modal = createElement('div', 'modal cost-limit-dialog');
  modal.append(createElement('h3', '', title || '设置用户限额'));
  const grid = createElement('div', 'cost-limit-dialog-grid');
  resources.forEach((resource) => grid.append(createLimitResourceForm(resource, seedLimits[resource.key])));
  const actions = createElement('div', 'modal-actions');
  const cancel = createElement('button', 'btn btn-secondary', '取消');
  cancel.type = 'button';
  cancel.dataset.action = 'cancel';
  const save = createElement('button', 'btn btn-primary', '保存');
  save.type = 'button';
  save.dataset.action = 'save';
  actions.append(cancel, save);
  modal.append(grid, actions);
  dialog.append(modal);
  return dialog;
}
